import { Fragment, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import { ChevronRight, ClipboardList, Coins, Package, Plus } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { SearchableSelect } from "~/components/ui/searchable-select";
import {
  StatCardsSkeleton,
  TableSkeleton,
} from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { TablePagination } from "~/components/ui/table-pagination";
import { usePagination } from "~/hooks/usePagination";
import { cn } from "~/lib/utils";
import {
  useMaquilaClientList,
  useMaquilaConsumptions,
} from "~/hooks/maquila";
import type { MaquilaConsumptionFilters } from "~/types/maquila";
import { formatCurrency, formatDate } from "~/utils/helpers";

const PAGE_SIZE = 20;

const numberFormatter = new Intl.NumberFormat("es-CL", {
  maximumFractionDigits: 2,
});

export const MaquilaConsumptionsPage = () => {
  const navigate = useNavigate();
  const { data: clients = [] } = useMaquilaClientList();
  const [filters, setFilters] = useState<MaquilaConsumptionFilters>({});
  const { data: consumptions = [], isLoading } = useMaquilaConsumptions(filters);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const kpis = useMemo(() => {
    const totalCost = consumptions.reduce(
      (sum, c) => sum + (c.total_cost_clp || 0),
      0
    );
    const clientCount = new Set(consumptions.map((c) => c.client_id)).size;
    return { count: consumptions.length, totalCost, clientCount };
  }, [consumptions]);

  const { page, setPage, pageCount, pageItems, total } = usePagination(
    consumptions,
    PAGE_SIZE
  );

  const refNumber = (n: number) => `MC-${String(n).padStart(5, "0")}`;

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader
          title="Consumos de Maquila"
          description="Historial y costo de los insumos rebajados por consumo de maquila"
        />
        <StatCardsSkeleton count={3} />
        <TableSkeleton rows={8} columns={6} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Consumos de Maquila"
        description="Historial y costo de los insumos de Aliace rebajados por consumo de maquila"
        back={
          <Button
            variant="ghost"
            className="h-8 px-2 text-muted-foreground"
            onClick={() => navigate("/maquila")}
          >
            ← Volver
          </Button>
        }
        actions={
          <Button onClick={() => navigate("/maquila/consumptions/new")}>
            <Plus className="mr-2 h-4 w-4" />
            Registrar consumo
          </Button>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <div className="rounded-lg bg-muted p-2">
              <ClipboardList className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Consumos</p>
              <p className="text-xl font-semibold">{kpis.count}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <div className="rounded-lg bg-muted p-2">
              <Coins className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Costo total</p>
              <p className="text-xl font-semibold">
                ${formatCurrency(kpis.totalCost)}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <div className="rounded-lg bg-muted p-2">
              <Package className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Clientes</p>
              <p className="text-xl font-semibold">{kpis.clientCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="w-full sm:w-64">
          <SearchableSelect
            value={filters.client_id ?? ""}
            onValueChange={(v) =>
              setFilters((f) => ({ ...f, client_id: v || undefined }))
            }
            options={[
              { value: "", label: "Todos los clientes" },
              ...clients.map((c) => ({ value: c.id, label: c.name })),
            ]}
            placeholder="Cliente"
          />
        </div>
        <Input
          className="h-9 sm:w-40"
          type="date"
          value={filters.date_from ?? ""}
          onChange={(e) =>
            setFilters((f) => ({ ...f, date_from: e.target.value || undefined }))
          }
        />
        <Input
          className="h-9 sm:w-40"
          type="date"
          value={filters.date_to ?? ""}
          onChange={(e) =>
            setFilters((f) => ({ ...f, date_to: e.target.value || undefined }))
          }
        />
        {(filters.client_id || filters.date_from || filters.date_to) && (
          <Button
            variant="ghost"
            className="h-9 text-muted-foreground"
            onClick={() => setFilters({})}
          >
            Limpiar
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          {consumptions.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={ClipboardList}
                title="Sin consumos registrados"
                description="Registra un consumo de maquila para rebajar los insumos entregados al cliente."
                action={
                  <Button onClick={() => navigate("/maquila/consumptions/new")}>
                    <Plus className="mr-2 h-4 w-4" />
                    Registrar consumo
                  </Button>
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead className="w-24">N°</TableHead>
                    <TableHead className="w-28">Fecha</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Producto</TableHead>
                    <TableHead className="w-24 text-right">Cantidad</TableHead>
                    <TableHead className="w-16 text-center">Planta</TableHead>
                    <TableHead className="w-32 text-right">Costo total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageItems.map((c) => {
                    const isOpen = expanded.has(c.id);
                    return (
                      <Fragment key={c.id}>
                        <TableRow
                          className="cursor-pointer hover:bg-muted/30"
                          onClick={() => toggleExpand(c.id)}
                        >
                          <TableCell>
                            <ChevronRight
                              className={cn(
                                "h-4 w-4 text-muted-foreground transition-transform duration-200",
                                isOpen && "rotate-90"
                              )}
                            />
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {refNumber(c.consumption_number)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDate(c.consumption_date)}
                          </TableCell>
                          <TableCell>{c.client?.name ?? "—"}</TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span>{c.product_name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            {numberFormatter.format(c.quantity_produced)}
                          </TableCell>
                          <TableCell className="text-center text-muted-foreground">
                            {c.plant}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            ${formatCurrency(c.total_cost_clp)}
                          </TableCell>
                        </TableRow>
                        {isOpen && (
                          <TableRow className="duration-200 animate-in fade-in-0 slide-in-from-top-1">
                            <TableCell colSpan={8} className="bg-muted/20 p-0">
                              <div className="px-6 py-3">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Insumo</TableHead>
                                      <TableHead className="text-right">Cantidad</TableHead>
                                      <TableHead className="w-16">Unidad</TableHead>
                                      <TableHead className="text-right">Costo unit.</TableHead>
                                      <TableHead className="text-right">Costo total</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {(c.items ?? []).map((item) => {
                                      const name =
                                        item.oil_type?.name ||
                                        item.raw_material?.name ||
                                        "Insumo";
                                      const code =
                                        item.oil_type?.code ||
                                        item.raw_material?.code ||
                                        null;
                                      return (
                                        <TableRow key={item.id}>
                                          <TableCell>
                                            <span className="font-medium">{name}</span>
                                            {code && (
                                              <span className="ml-2 font-mono text-xs text-muted-foreground">
                                                {code}
                                              </span>
                                            )}
                                          </TableCell>
                                          <TableCell className="text-right">
                                            {numberFormatter.format(item.quantity)}
                                          </TableCell>
                                          <TableCell className="text-muted-foreground">
                                            {item.unit}
                                          </TableCell>
                                          <TableCell className="text-right text-muted-foreground">
                                            ${formatCurrency(item.unit_cost_clp ?? 0)}
                                          </TableCell>
                                          <TableCell className="text-right">
                                            ${formatCurrency(item.total_cost_clp ?? 0)}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                                {c.notes && (
                                  <p className="mt-2 text-sm text-muted-foreground">
                                    <span className="font-medium">Notas:</span> {c.notes}
                                  </p>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {consumptions.length > PAGE_SIZE && (
        <TablePagination
          page={page}
          pageCount={pageCount}
          totalItems={total}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          itemLabel="consumos"
        />
      )}
    </PageContainer>
  );
};
